/**
 * useMessageSender Hook
 *
 * Manages message sending operations including text, interactive messages,
 * and messages with media attachments. Handles duplicate prevention and
 * callback routing.
 */

import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import firebase from 'firebase/compat/app';

// Redux
import * as chatV2 from 'src/app/modules/chat/redux/ChatSlice.v2';

// Actions
import * as actAccount from 'src/actions/account';
import * as Chat from 'src/actions/chat';
import * as Log from 'src/util/SDayslogger';

// Models
import Account from 'src/app/models/Account';
import { Room, Collaboration } from 'src/app/models/Chat';
import Message, {
  InteractiveMessage,
  InteractiveMessageButton,
} from 'src/app/models/Message';
import Language from 'src/app/models/Language';

// Database
import db, { Timestamp } from 'src/db';
import { createRef } from 'src/db/connection';
import { getAccountByID } from 'src/db/serviceAccount';

// Utils
import { formatTemplate } from 'src/util/Formatter';
import * as lc from 'src/app/modules/localstorage/index';

// Types
type FileMediaData =
  | { url?: string; id?: string }
  | undefined;

export interface UseMessageSenderParams {
  selectedCollaboration: Collaboration;
  selectedRoom: Room | undefined;
  replyToMessage: Message | null;
  orderInitiateMessage: any;
  file: File | undefined;
  inputFileName: string;
  fileSize: string;
  messageType: string | undefined;
  uploadFile: (companyID: string) => Promise<FileMediaData>;
  uploadMediaFallback: () => Promise<FileMediaData>;
}

export interface UseMessageSenderReturn {
  isSending: boolean;
  sendMessage: (message: string, onClearState: () => void, templateLanguage?: Language) => Promise<void>;
}

export const useMessageSender = ({
  selectedCollaboration,
  selectedRoom,
  replyToMessage,
  orderInitiateMessage,
  file,
  inputFileName,
  fileSize,
  messageType,
  uploadFile,
  uploadMediaFallback,
}: UseMessageSenderParams): UseMessageSenderReturn => {
  const dispatch = useDispatch();
  const nav = useNavigate();

  const [isSending, setIsSending] = useState(false);
  const [lastSentMessage, setLastSentMessage] = useState<string | null>(null);

  /**
   * Send text message with optional media
   */
  const sendMessageFromChat = useCallback(
    async (message: string, onClearState: () => void) => {
      let fileMedia: FileMediaData = undefined;
      let firebaseMediaURL: string | undefined = undefined;

      if (messageType !== 'text') {
        try {
          fileMedia = await uploadFile(String(selectedCollaboration?.company?.id));
          firebaseMediaURL = fileMedia?.url;

          // If upload succeeded but returned no URL, try platform media endpoint
          if (!firebaseMediaURL) {
            console.warn(
              'Firebase upload returned no URL, trying platform media endpoint...'
            );
            const platformUploadResult = await uploadMediaFallback();
            if (platformUploadResult?.url) {
              firebaseMediaURL = platformUploadResult.url;
              fileMedia = {
                url: platformUploadResult.url,
                id: platformUploadResult.id,
              };
            }
          }
        } catch (error) {
          console.warn(
            'Firebase upload failed, trying platform media endpoint:',
            error
          );
          const platformUploadResult = await uploadMediaFallback();
          if (platformUploadResult?.url) {
            firebaseMediaURL = platformUploadResult.url;
            fileMedia = {
              url: platformUploadResult.url,
              id: platformUploadResult.id,
            };
          } else {
            console.error(
              'Platform media upload also failed, will continue without media'
            );
          }
        }
      }

      if (messageType === 'text' && message === '') {
        if (orderInitiateMessage?.marketplaceType && orderInitiateMessage?.id) {
          await Chat.createMessageInitiateChatMarketplace(
            orderInitiateMessage.marketplaceType.toString(),
            orderInitiateMessage.id
          );
        }
        return;
      }

      if (orderInitiateMessage?.marketplaceType && orderInitiateMessage?.id) {
        await Chat.createMessageInitiateChatMarketplace(
          orderInitiateMessage.marketplaceType.toString(),
          orderInitiateMessage.id
        );
      }

      const client = lc.getItemLC(lc.LCName.Client);
      const clientID: string = client?.id;

      const roomRef = db
        .collection('collaborations')
        .doc(selectedCollaboration?.id)
        .collection('rooms')
        .doc(selectedRoom?.id);

      const newMessage: Message = {
        client: createRef('clients', clientID),
        channel: selectedRoom?.channel,
        createdAt: Timestamp.now(),
        destination: 'outbound',
        customer: selectedCollaboration?.customer,
        customerPhoneNumber:
          selectedCollaboration?.phoneNumber || selectedRoom?.userSearchKey,
        ...(selectedCollaboration?.toUser && {
          user: selectedCollaboration.toUser,
        }),
        mediaUrl: firebaseMediaURL,
        isActive: true,
        messageType,
        textContent: message.trim(),
        previewurl: true,
        updatedAt: Timestamp.now(),
        voice: false,
        filename: inputFileName,
        filesize: fileSize,
        collaboration: createRef('collaborations', selectedCollaboration?.id),
        room: roomRef,
        replyMessageID: replyToMessage
          ? roomRef.collection('messages').doc(replyToMessage.id)
          : undefined,
      };

      if (fileMedia) {
        newMessage.fileId = fileMedia.id;
      }

      const onFetchAccountFinished = async (accountChat: Account | undefined) => {
        if (!accountChat) return;

        if (!newMessage.channel) {
          newMessage.channel = accountChat.type;
        }

        // If Firebase upload was skipped, attach file data to message for API handling
        if (file && !firebaseMediaURL) {
          (newMessage as any).__fileForUpload = file;
        }

        await Chat.createCollaborationMessage(
          newMessage,
          String(selectedCollaboration?.company?.id),
          selectedCollaboration?.id,
          accountChat,
          selectedCollaboration?.customerModel,
          handleMessageCallback,
          false,
          selectedCollaboration?.roomsModel
        );
      };

      if (selectedRoom?.account?.id) {
        const account = (await getAccountByID(
          selectedRoom.account.id
        )) as Account;
        if (account) await onFetchAccountFinished(account);
      } else {
        const accounts = await actAccount.fetchAccountByCompanyAndChannel(
          String(selectedCollaboration?.company?.id),
          selectedRoom?.channel
        );
        if (accounts?.length > 0) await onFetchAccountFinished(accounts[0]);
      }

      // Clear state
      onClearState();
    },
    [
      messageType,
      file,
      uploadFile,
      uploadMediaFallback,
      selectedCollaboration,
      selectedRoom,
      orderInitiateMessage,
      replyToMessage,
      inputFileName,
      fileSize,
    ]
  );

  /**
   * Message callback handler
   */
  const handleMessageCallback = useCallback(
    async (responseMessage: Message, error: string) => {
      if (!responseMessage) return;

      if (selectedCollaboration) {
        dispatch(chatV2.setActiveTab('myChat'));
        dispatch(
          chatV2.setSelectedCollaboration({
            chatType: 'myChat',
            id: selectedCollaboration?.id,
          })
        );
        dispatch(
          chatV2.setSelectedRoom({ chatType: 'myChat', id: selectedRoom?.id })
        );
        dispatch(
          chatV2.removeCollaboration({
            chatType: 'allChat',
            id: selectedCollaboration?.id,
          })
        );
      }
    },
    [dispatch, selectedCollaboration, selectedRoom?.id]
  );

  /**
   * Send interactive message (template-based)
   */
  const sendInteractiveMessage = useCallback(
    async (templateLanguage: Language) => {
      const client = lc.getItemLC(lc.LCName.Client);
      const clientID: string = client?.id;

      const interactive: InteractiveMessage = {
        type: 'button',
        components: [],
      };

      const formattedTemplate = formatTemplate(templateLanguage);

      if (templateLanguage.header) {
        const header = templateLanguage.header;
        const headerObj: any = { type: 'header' };

        if (header.type === 'text') headerObj.text = formattedTemplate.header;
        if (header.type === 'media') {
          if (header.mediaType === 'image') headerObj.image = header.headerURL;
          if (header.mediaType === 'video') headerObj.video = header.headerURL;
          if (header.mediaType === 'document') {
            headerObj.document = header.headerURL;
            headerObj.filename = header.fileName || header.filename;
          }
        }
        interactive.components.push(headerObj);
      }

      let textContent = '';
      if (templateLanguage.body) {
        interactive.components.push({
          type: 'body',
          text: formattedTemplate.body,
        });
        textContent = formattedTemplate?.body?.replaceAll('\n', '</br>') ?? '';
      }

      if (templateLanguage.footer) {
        interactive.components.push({
          type: 'footer',
          text: templateLanguage.footer,
        });
      }

      if (templateLanguage.buttons) {
        const buttons: InteractiveMessageButton[] =
          templateLanguage.buttons.map((button) => ({
            id: `${button.index!}`,
            title: button.text,
          }));
        interactive.components.push({ type: 'button', buttons });
      }

      const roomRef = db
        .collection('collaborations')
        .doc(selectedCollaboration?.id)
        .collection('rooms')
        .doc(selectedRoom?.id);

      const newMessage: Message = {
        client: createRef('clients', clientID),
        channel: selectedRoom?.channel,
        createdAt: Timestamp.now(),
        destination: 'outbound',
        customer: selectedCollaboration?.customer,
        customerPhoneNumber:
          selectedCollaboration?.phoneNumber || selectedRoom?.userSearchKey,
        ...(selectedCollaboration?.toUser && {
          user: selectedCollaboration.toUser,
        }),
        isActive: true,
        messageType: 'interactive',
        interactive,
        textContent,
        header: formattedTemplate.header,
        footer: formattedTemplate.footer,
        buttons: formattedTemplate.buttons,
        previewurl: true,
        updatedAt: Timestamp.now(),
        voice: false,
        collaboration: createRef('collaborations', selectedCollaboration?.id),
        room: roomRef,
        replyMessageID: replyToMessage
          ? roomRef.collection('messages').doc(replyToMessage.id)
          : undefined,
      };

      const onFetchAccountFinished = async (
        accountChat: Account | undefined
      ) => {
        if (!accountChat) return;

        await Chat.createCollaborationMessage(
          newMessage,
          String(selectedCollaboration?.company?.id),
          selectedCollaboration?.id,
          accountChat,
          selectedCollaboration?.customerModel,
          handleMessageCallback,
          false,
          selectedCollaboration?.roomsModel
        ).catch(() => {
          Log.errorWithNavigate(
            nav,
            'Failed to Create Collaboration message : ' +
              selectedCollaboration?.id
          );
        });
      };

      if (selectedRoom?.account?.id) {
        const account = (await getAccountByID(
          selectedRoom.account.id
        )) as Account;
        if (account) await onFetchAccountFinished(account);
      } else {
        const accounts = await actAccount.fetchAccountByCompanyAndChannel(
          String(selectedCollaboration?.company?.id),
          selectedRoom?.channel
        );
        if (accounts?.length > 0) await onFetchAccountFinished(accounts[0]);
      }
    },
    [
      selectedCollaboration,
      selectedRoom,
      replyToMessage,
      handleMessageCallback,
      nav,
    ]
  );

  /**
   * Main send message orchestrator
   */
  const sendMessage = useCallback(
    async (
      message: string,
      onClearState: () => void,
      templateLanguage?: Language
    ) => {
      // Skip if already sending
      if (isSending) return;

      // Skip duplicate text-only messages (but always allow file sends)
      if (!file && message.trim() === '' && message.trim() === lastSentMessage)
        return;

      setIsSending(true);
      try {
        if (!templateLanguage) await sendMessageFromChat(message, onClearState);
        if (templateLanguage) await sendInteractiveMessage(templateLanguage);
        setLastSentMessage(message.trim());
      } finally {
        setIsSending(false);
      }
    },
    [
      isSending,
      file,
      lastSentMessage,
      sendMessageFromChat,
      sendInteractiveMessage,
    ]
  );

  return {
    isSending,
    sendMessage,
  };
};
